"use client";

import { Country } from "asosteam";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { getCookie, setCookie } from "@repo/client-cookies";
import { useStore } from "@repo/ui/store";

const ONE_YEAR_IN_MINUTES = 525600;

export function ClientContextManager() {
  const {
    countryData,
    keyStoreDataversion: serverKeyStoreDataversion,
    versionManifest,
  } = useStore();
  const pathname = usePathname();
  const setBrowseCookies = (countryCode: string, countryData: Country) => {
    setCookie("browseCountry", countryCode, {
      expiresInMinutes: ONE_YEAR_IN_MINUTES,
    });
    setCookie("storeCode", countryData.storeCode, {
      expiresInMinutes: ONE_YEAR_IN_MINUTES,
    });
    setCookie("browseCurrency", countryData.defaultCurrency, {
      expiresInMinutes: ONE_YEAR_IN_MINUTES,
    });
    setCookie("browseLanguage", countryData.defaultLanguage, {
      expiresInMinutes: ONE_YEAR_IN_MINUTES,
    });
    setCookie("browseSizeSchema", countryData.defaultSizeSchema, {
      expiresInMinutes: ONE_YEAR_IN_MINUTES,
    });
  };

  // Handle keyStoreDataversion cookie
  useEffect(
    () => {
      if (!versionManifest) {
        return;
      }

      const keyStoreDataversionCookie = getCookie("keyStoreDataversion");
      const keyStoreDataversionToUse =
        versionManifest.useCurrentKeyStoreDataversion
          ? versionManifest.keyStoreDataversion.current
          : versionManifest.keyStoreDataversion.previous;
      const permittedKeyStoreDataversions =
        versionManifest.permittedKeyStoreDataversions;

      if (!keyStoreDataversionCookie) {
        // Set the keyStoreDataversion cookie according to the manifest
        setCookie("keyStoreDataversion", keyStoreDataversionToUse, {
          expiresInMinutes: 60,
        });
      } else if (
        permittedKeyStoreDataversions.includes(keyStoreDataversionCookie)
      ) {
        // Reset the cookies to extend the expiry
        setCookie("keyStoreDataversion", keyStoreDataversionCookie, {
          expiresInMinutes: 60,
        });
      } else if (
        !permittedKeyStoreDataversions.includes(keyStoreDataversionCookie)
      ) {
        // Update the keyStoreDataversion cookie
        console.warn(
          `keyStoreDataversion cookie (${keyStoreDataversionCookie}) is invalid, updating to ${keyStoreDataversionToUse}`,
        );
        setCookie("keyStoreDataversion", keyStoreDataversionToUse, {
          expiresInMinutes: 60,
        });
        window.location.reload();
      }
    },
    // pathname is used to trigger the effect when the page changes
    // and the ensure the cookie is set correctly
    [versionManifest, serverKeyStoreDataversion, pathname],
  );

  // Handle browse cookies
  useEffect(() => {
    if (!countryData) {
      return;
    }

    const geoCountryCookie = getCookie("geocountry");
    const browseCountryCookie = getCookie("browseCountry");

    if (geoCountryCookie && !browseCountryCookie) {
      if (geoCountryCookie === countryData.countryCode) {
        // Set the browseCountry to the geocountry if it matches
        // the countryData.countryCode
        setBrowseCookies(countryData.countryCode, countryData);
      } else {
        // TODO: Implement AB#1060101
        setBrowseCookies(countryData.countryCode, countryData);
        console.warn(
          `The geocountry cookie "${geoCountryCookie}" does not match the countryData "${countryData.countryCode}". We will display a notification here.`,
        );
      }
    } else if (geoCountryCookie && browseCountryCookie !== geoCountryCookie) {
      // TODO: Implemented AB#1060101
      console.warn(
        `The browseCountry cookie "${browseCountryCookie}" does not match the geocountry cookie "${geoCountryCookie}". We will display a notification here.`,
      );
      setBrowseCookies(geoCountryCookie, countryData);
    } else if (browseCountryCookie) {
      // Reset the cookies to extend the expiry
      setBrowseCookies(browseCountryCookie, countryData);
    }
  }, [countryData]);

  return null;
}
